import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import {
  getAccessToken,
  getUserId,
  getOrganizationId,
} from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";
import { sortSupportMessagesChronologically } from "@/utils/supportUtils";

const getSupportTicketParentId = (record) => {
  if (!record?.supportTicketID) return null;
  return record.supportTicketID._id ?? record.supportTicketID;
};

const filterRepliesForTicket = (records = [], ticketId) => {
  if (!ticketId) return [];

  const ticketIdStr = String(ticketId);
  return records.filter((record) => {
    const parentId = getSupportTicketParentId(record);
    return parentId && String(parentId) === ticketIdStr;
  });
};

const extractTicketReplies = (payload, ticketId) => {
  if (Array.isArray(payload?.tickets)) {
    return payload.tickets;
  }

  const dataArray = Array.isArray(payload?.data) ? payload.data : [];
  const nestedReplies = dataArray.find((item) => Array.isArray(item?.tickets))?.tickets;
  if (nestedReplies) {
    return nestedReplies;
  }

  return filterRepliesForTicket(dataArray, ticketId);
};

export const fetchSupportTicketsAsync = createAsyncThunk(
  "support/fetchTickets",
  async ({ skip = 0, limit = 10 }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Try to get organization ID
      const organizationId = getOrganizationId();

      // Construct the URL based on whether we have an organization ID
      let url = `${BASE_URL}/v1/supportTicket?skip=${skip}&limit=${limit}`;
      if (organizationId) {
        url += `&organizationID=${organizationId}`;
      }

      const response = await secureAxios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Shared POST body builder for new tickets and admin replies (same endpoint).
const buildSupportTicketPayload = (ticketData) => {
  const userID = getUserId();
  const organizationID = getOrganizationId();

  const payload = {
    userID,
    organizationID,
    description: ticketData.description,
  };

  if (ticketData.supportTicketID) {
    payload.supportTicketID = ticketData.supportTicketID;
    return payload;
  }


  // New ticket
  return {
    ...payload,
    subject: ticketData.title,
    priority: ticketData.type,
    status: "Open",
  };
};

export const createSupportTicketAsync = createAsyncThunk(
  "support/createTicket",
  async (ticketData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      if (!getUserId()) {
        throw new Error("No user ID found");
      }

      const supportTicket = buildSupportTicketPayload(ticketData);

      const response = await secureAxios.post(
        `${BASE_URL}/v1/supportTicket`,
        supportTicket,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const replySupportTicketAsync = createAsyncThunk(
  "support/replyTicket",
  async ({ supportTicketID, description }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const userID = getUserId();
      const organizationID = getOrganizationId();

      if (!token) {
        throw new Error("No authentication token found");
      }

      if (!userID) {
        throw new Error("No user ID found");
      }

      // Plain JSON object sent as the HTTP request body (axios `data` param).
      const requestBody = {
        userID,
        organizationID,
        supportTicketID,
        description,
      };


      const response = await secureAxios.post(
        `${BASE_URL}/v1/supportTicket`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchSingleTicketAsync = createAsyncThunk(
  "support/fetchSingleTicket",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.get(
        `${BASE_URL}/v1/supportTicket/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchTicketRepliesAsync = createAsyncThunk(
  "support/fetchTicketReplies",
  async ({ ticketId }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const organizationId = getOrganizationId();
      const params = { ticket: ticketId };
      if (organizationId) {
        params.organizationID = organizationId;
      }

      const response = await secureAxios.get(`${BASE_URL}/v1/supportTicket`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });


      const payload = response.data || {};
      const replies = sortSupportMessagesChronologically(
        Array.isArray(payload.data)
          ? payload.data
          : extractTicketReplies(payload, ticketId)
      );

      return { data: replies, ticketId };
    } catch (error) {
      if (error.response?.status === 404) {
        return { data: [], ticketId };
      }
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const updateTicketStatusAsync = createAsyncThunk(
  "support/updateTicketStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const organizationID = getOrganizationId();

      if (!token) {
        throw new Error("No authentication token found");
      }

      if (!organizationID) {
        throw new Error("No organization ID found");
      }

      const response = await secureAxios.patch(
        `${BASE_URL}/v1/supportTicket/${id}/${organizationID}/${status}`,
        {}, // Empty object or null as the request body (no data needed)
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return { ...response.data, id, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchTicketsByAny = createAsyncThunk(
  "support/fetchTicketsByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.get(
        `${BASE_URL}/v1/supportTicket/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const rawData = response.data.data || [];
      const data =
        key === "supportTicketID"
          ? filterRepliesForTicket(rawData, value)
          : rawData;

      return {
        data,
        total: data.length,
        isSingleTicket: key === "_id" || key === "id",
      };
    } catch (error) {
      // If it's a 404 error, return empty array instead of rejecting
      if (error.response?.status === 404) {
        return { data: [], total: 0, isSingleTicket: false };
      }
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  isLoading: false,
  error: null,
  success: false,
  currentTicket: null,
  tickets: [],
  fetchedTickets: [],
  pagination: {
    skip: 0,
    limit: 10,
    total: 0,
  },
  singleTicket: null,
  singleTicketLoading: false,
  ticketReplies: [],
  ticketRepliesLoading: false,
  replyLoading: false,
  replySuccess: false,
  statusUpdateLoading: false,
  statusUpdateSuccess: false,
  isOfflineData: false,
};

const supportSlice = createSlice({
  name: "support",
  initialState,
  reducers: {
    clearSupportState: (state) => {
      state.error = null;
      state.success = false;
      state.currentTicket = null;
      state.singleTicket = null;
    },
    setPagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
    clearStatusUpdateSuccess: (state) => {
      state.statusUpdateSuccess = false;
    },
    clearReplySuccess: (state) => {
      state.replySuccess = false;
    },
    clearTicketReplies: (state) => {
      state.ticketReplies = [];
    },
    loadCachedData: (state, action) => {
      state.tickets = action.payload.tickets || [];
      state.isOfflineData = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSupportTicketAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createSupportTicketAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;

        // Format the new ticket to ensure it matches the expected structure
        const newTicket = {
          _id: action.payload.data._id,
          id: action.payload.data.id, // For consistency with ticket_id
          subject: action.payload.data.subject,
          description: action.payload.data.description,
          status: action.payload.data.status || "Open",
          priority: action.payload.data.priority,
          userID: action.payload.data.userID,
          organizationID: action.payload.data.organizationID,
          createdAt: action.payload.data.createdAt || new Date().toISOString(),
          updatedAt: action.payload.data.updatedAt || new Date().toISOString(),
        };

        // Add to both arrays to ensure UI is updated
        if (Array.isArray(state.tickets)) {
          state.tickets.unshift(newTicket);
        } else {
          state.tickets = [newTicket];
        }

        if (Array.isArray(state.fetchedTickets)) {
          state.fetchedTickets.unshift(newTicket);
        } else {
          state.fetchedTickets = [newTicket];
        }

        // Increment total in pagination if available
        if (state.pagination && typeof state.pagination.total === "number") {
          state.pagination.total += 1;
        }
      })
      .addCase(createSupportTicketAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = false;
      })
      .addCase(fetchSupportTicketsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSupportTicketsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tickets = action.payload.data;
        state.pagination.total =
          action.payload.total || action.payload.data.length;
      })
      .addCase(fetchSupportTicketsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchSingleTicketAsync.pending, (state) => {
        state.singleTicketLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleTicketAsync.fulfilled, (state, action) => {
        state.singleTicketLoading = false;
        const ticketId = action.meta.arg?.value;
        const tickets = action.payload?.data || [];

        state.singleTicket =
          tickets.find((item) => String(item._id) === String(ticketId)) ||
          tickets[0] ||
          null;
      })
      .addCase(fetchSingleTicketAsync.rejected, (state, action) => {
        state.singleTicketLoading = false;
        state.error = action.payload;
      })
      // Replies via GET /v1/supportTicket?ticket={id}
      .addCase(fetchTicketRepliesAsync.pending, (state) => {
        state.ticketRepliesLoading = true;
        state.ticketReplies = [];
      })
      .addCase(fetchTicketRepliesAsync.fulfilled, (state, action) => {
        state.ticketRepliesLoading = false;
        state.ticketReplies = sortSupportMessagesChronologically(
          action.payload.data || []
        );
      })
      .addCase(fetchTicketRepliesAsync.rejected, (state, action) => {
        state.ticketRepliesLoading = false;
        state.error = action.payload;
      })
      // Replies linked to a ticket via supportTicketID
      .addCase(fetchTicketsByAny.pending, (state, action) => {
        if (action.meta.arg?.key === "supportTicketID") {
          state.ticketRepliesLoading = true;
          state.ticketReplies = [];
          return;
        }
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTicketsByAny.fulfilled, (state, action) => {
        if (action.meta.arg?.key === "supportTicketID") {
          state.ticketRepliesLoading = false;
          state.ticketReplies = sortSupportMessagesChronologically(
            action.payload.data || []
          );
          return;
        }

        state.isLoading = false;
        state.fetchedTickets = action.payload.data;

        if (action.payload.isSingleTicket && action.payload.data.length > 0) {
          state.singleTicket = action.payload.data[0];
        }

        if (!action.payload.isSingleTicket) {
          state.pagination.total = action.payload.total;
        }

        state.error = null;
        state.isOfflineData = false;
      })
      .addCase(fetchTicketsByAny.rejected, (state, action) => {
        if (action.meta.arg?.key === "supportTicketID") {
          state.ticketRepliesLoading = false;
          return;
        }
        state.isLoading = false;
        state.error = action.payload;
      })
      // Admin reply
      .addCase(replySupportTicketAsync.pending, (state) => {
        state.replyLoading = true;
        state.error = null;
        state.replySuccess = false;
      })
      .addCase(replySupportTicketAsync.fulfilled, (state, action) => {
        state.replyLoading = false;
        state.replySuccess = true;
        state.error = null;

        const reply = action.payload?.data;
        const parentId = getSupportTicketParentId(reply);
        if (reply && parentId) {
          state.ticketReplies = sortSupportMessagesChronologically([
            ...state.ticketReplies,
            reply,
          ]);
        }
      })
      .addCase(replySupportTicketAsync.rejected, (state, action) => {
        state.replyLoading = false;
        state.error = action.payload;
        state.replySuccess = false;
      })
      .addCase(updateTicketStatusAsync.pending, (state) => {
        state.statusUpdateLoading = true;
        state.error = null;
        state.statusUpdateSuccess = false;
      })
      .addCase(updateTicketStatusAsync.fulfilled, (state, action) => {
        state.statusUpdateLoading = false;
        state.statusUpdateSuccess = true;

        const ticketId = action.payload.id || action.payload.data._id;
        const ticketIndex = state.tickets.findIndex(
          (ticket) => ticket._id === ticketId || ticket.id === ticketId
        );

        if (ticketIndex !== -1) {
          state.tickets[ticketIndex].status =
            action.payload.status || action.payload.data.status;
        }
      })
      .addCase(updateTicketStatusAsync.rejected, (state, action) => {
        state.statusUpdateLoading = false;
        state.error = action.payload;
        state.statusUpdateSuccess = false;
      });
  },
});

export const {
  clearSupportState,
  setPagination,
  clearStatusUpdateSuccess,
  clearReplySuccess,
  clearTicketReplies,
  loadCachedData,
} = supportSlice.actions;

// Selectors
export const selectSupportIsLoading = (state) => state.support.isLoading;
export const selectSupportError = (state) => state.support.error;
export const selectSupportSuccess = (state) => state.support.success;
export const selectCurrentTicket = (state) => state.support.currentTicket;
export const selectAllTickets = (state) => state.support.tickets;
export const selectFetchedTickets = (state) =>
  state.support.fetchedTickets || [];
export const selectPagination = (state) => state.support.pagination;
export const selectSingleTicket = (state) => state.support.singleTicket;
export const selectSingleTicketLoading = (state) =>
  state.support.singleTicketLoading;
export const selectStatusUpdateLoading = (state) =>
  state.support.statusUpdateLoading;
export const selectStatusUpdateSuccess = (state) =>
  state.support.statusUpdateSuccess;
export const selectTicketReplies = (state) => state.support.ticketReplies;
export const selectTicketRepliesLoading = (state) =>
  state.support.ticketRepliesLoading;
export const selectReplyLoading = (state) => state.support.replyLoading;
export const selectReplySuccess = (state) => state.support.replySuccess;
export const selectIsOfflineData = (state) =>
  state.support.isOfflineData || false;

export default supportSlice.reducer;
